import { ApiClient } from '../utils/api';
import { ApiResponse } from '../types';

export class AuthService {
  private apiClient: ApiClient;
  private isBrowser: boolean;
  private isLocalStorageAvailable: boolean;
  private sessionTokenKey: string;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
    this.isBrowser = typeof window !== 'undefined';
    this.isLocalStorageAvailable = this.isBrowser && 
      (typeof (window as any).__oncadeLocalStorageAvailable !== 'undefined' 
        ? (window as any).__oncadeLocalStorageAvailable 
        : this.checkLocalStorageAvailability());
    this.sessionTokenKey = 'oncade_session_token';
    
    // Initialize session token from localStorage if available
    const storedToken = this.getSessionToken();
    if (storedToken) {
      this.apiClient.setSessionToken(storedToken);
    }
  }

  /**
   * Check if localStorage is available in the current environment
   * @returns Boolean indicating if localStorage is available
   */
  private checkLocalStorageAvailability(): boolean {
    if (!this.isBrowser) return false;
    
    try {
      const testKey = '__oncade_test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      console.warn('localStorage is not available in this environment');
      return false;
    }
  }

  /**
   * Initialize the game server by verifying the game ID and API key
   * @param gameId Game ID to initialize
   * @param apiKey API key for authentication
   * @returns Promise resolving to a boolean indicating success
   */
  async initializeGameServer(gameId: string, apiKey: string): Promise<boolean> {
    try {
      // Get the stored session token if available
      const sessionToken = this.getSessionToken();
      
      const response = await this.apiClient.post<{ 
        authenticated: boolean;
        sessionToken?: string;
        expiresAt?: string;
      }>('/api/auth/initialize', {
        gameId,
        apiKey,
        sessionToken
      });

      // If initialization was successful and we received a session token, store it
      if (response.success && response.data?.authenticated === true) {
        if (response.data.sessionToken) {
          this.storeSessionToken(response.data.sessionToken);
          // Also update the token in the API client
          this.apiClient.setSessionToken(response.data.sessionToken);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to initialize game server:', error);
      return false;
    }
  }

  /**
   * Authenticate a request using the API key
   * @param apiKey API key to validate
   * @returns Promise resolving to a boolean indicating if the API key is valid
   */
  async authenticateRequest(apiKey: string): Promise<boolean> {
    try {
      const response = await this.apiClient.post<{ authenticated: boolean }>('/api/auth/validate', {
        apiKey
      });

      return response.success && !!response.data?.authenticated;
    } catch (error) {
      console.error('Failed to authenticate request:', error);
      return false;
    }
  }

  /**
   * Store the session token in localStorage (browser) or memory (node)
   * @param token Session token to store
   */
  private storeSessionToken(token: string): void {
    if (this.isBrowser && this.isLocalStorageAvailable) {
      try {
        localStorage.setItem(this.sessionTokenKey, token);
      } catch (error) {
        console.error('Failed to store session token in localStorage:', error);
      }
    }
  }

  /**
   * Get the stored session token from localStorage (browser) or memory (node)
   * @returns The stored session token or null if not found
   */
  public getSessionToken(): string | null {
    if (this.isBrowser && this.isLocalStorageAvailable) {
      try {
        return localStorage.getItem(this.sessionTokenKey);
      } catch (error) {
        console.error('Failed to get session token from localStorage:', error);
      }
    }
    return null;
  }

  /**
   * Get detailed session information including user association status
   * @returns Promise resolving to session information including user ID if associated
   */
  async getSessionInfo(): Promise<{
    isValid: boolean;
    hasUserId: boolean;
  }> {
    try {
      const response = await this.apiClient.get<{
        isValid: boolean;
        hasUserId: boolean;
      }>('/api/auth/session');

      if (response) {
        return response as any;
      }
      
      return {
        isValid: false,
        hasUserId: false,
      };
    } catch (error) {
      console.error('Failed to get session info:', error);
      return {
        isValid: false,
        hasUserId: false,
      };
    }
  }

  /**
   * Clear the stored session token and user ID
   */
  clearSession(): void {
    if (this.isBrowser && this.isLocalStorageAvailable) {
      try {
        localStorage.removeItem(this.sessionTokenKey);
        localStorage.removeItem(`${this.sessionTokenKey}_user`);
      } catch (error) {
        console.error('Failed to clear session from localStorage:', error);
      }
    }
    
    // Also clear the token in the API client
    this.apiClient.setSessionToken(null);
  }
} 